import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { isDefined } from '../util.js';

// The color editor stores values from an <input type="color">, i.e. plain hex
// strings. Configured colors also end up in generated CSS text, so anything
// that is not a hex color (#rgb, #rgba, #rrggbb or #rrggbbaa) must be rejected
// before use.
export function isHexColor(value: Maybe<string>): value is `#${string}` {
  return !!value && /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);
}

export function mergeRegistrationColorDefinitions(
  ...colorDefinitions: Maybe<Partial<EntityData<RegistrationColorDefinitions>>>[]
): RegistrationColorDefinitions {
  const nonEmptyDefs = colorDefinitions.filter(isDefined);
  if (nonEmptyDefs.length === 0) throw new Error('at least one non empty definition has to be provided');

  const mergedData = {} as EntityData<RegistrationColorDefinitions>;

  for (const def of nonEmptyDefs) {
    const data = (() => {
      if (def instanceof AdornisEntity) return def.toObject?.();
      return def ?? {};
    })();

    for (const [key, value] of Object.entries(data)) {
      // apply only if not already set
      if (!!value && !mergedData[key]) mergedData[key] = value;
    }
  }

  return new RegistrationColorDefinitions(mergedData);
}

@Entity()
export class ColorDefinitions extends AdornisEntity {
  static override _class = 'ColorDefinitions';

  @Field(type => String, { default: v => v ?? '#00628f' })
  accentColor!: string;

  @Field(type => RegistrationColorDefinitions, { default: v => v ?? new RegistrationColorDefinitions({}) })
  registrationColorDefinitions!: RegistrationColorDefinitions;

  @Field(type => MailColorDefinitions, { default: v => v ?? new MailColorDefinitions({}) })
  mailColorDefinitions!: MailColorDefinitions;

  @Field(type => ProfileColorDefinitions, { default: v => v ?? new ProfileColorDefinitions({}) })
  profileColorDefinitions!: ProfileColorDefinitions;

  @Field(type => LoginColorDefinitions, { default: v => v ?? new LoginColorDefinitions({}) })
  loginColorDefinitions!: LoginColorDefinitions;
}

@Entity()
export class RegistrationColorDefinitions extends AdornisEntity {
  static override _class = 'RegistrationColorDefinitions';

  @Field(type => String)
  stepColor!: string;

  @Field(type => String)
  stepFontColor!: string;

  @Field(type => String)
  productColor!: string;

  @Field(type => String)
  productFontColor!: string;

  @Field(type => String)
  moreProductsColor!: string;

  @Field(type => String)
  moreProductsFontColor!: string;

  @Field(type => String)
  chipInfosColor!: string;

  @Field(type => String)
  chipInfosFontColor!: string;

  @Field(type => String)
  oldPriceColor!: string;

  @Field(type => String)
  newPriceColor!: string;

  @Field(type => String)
  layoutLightContrastColor!: string;

  @Field(type => String)
  warnColor!: string;
}

@Entity()
export class MailColorDefinitions extends AdornisEntity {
  static override _class = 'MailColorDefinitions';

  @Field(type => String)
  accentColor!: string;

  @Field(type => String)
  accentContrastFontColor!: string;
}

// Colors of the user profile page. All of them are optional: when unset, the
// elements keep their current default appearance (theme accent etc.).
@Entity()
export class ProfileColorDefinitions extends AdornisEntity {
  static override _class = 'ProfileColorDefinitions';

  // active sidebar/tabbar navigation entry (text + left border)
  @Field(type => String)
  navigationActiveColor!: string;

  // active tab (text + underline), e.g. upcoming/past events
  @Field(type => String)
  tabsColor!: string;

  // filled buttons, e.g. "Gesamtbescheinigung herunterladen"
  @Field(type => String)
  buttonColor!: string;

  @Field(type => String)
  buttonFontColor!: string;

  // outlined buttons (border + text), e.g. "Zu neuen Workshops anmelden"
  @Field(type => String)
  outlineButtonColor!: string;

  // calendar synchronisation banner
  @Field(type => String)
  calendarSyncColor!: string;

  @Field(type => String)
  calendarSyncFontColor!: string;

  // link field inside the calendar synchronisation banner
  @Field(type => String)
  calendarSyncFieldColor!: string;

  @Field(type => String)
  calendarSyncFieldFontColor!: string;
}

// Colors of the login / sign-in page. Optional: when unset, the buttons keep
// their current default appearance. Kept separate from the profile colors so
// the login page can be themed independently.
@Entity()
export class LoginColorDefinitions extends AdornisEntity {
  static override _class = 'LoginColorDefinitions';

  // filled buttons, e.g. "Mit E-Mail anmelden", DFN/IdP login
  @Field(type => String)
  buttonColor!: string;

  @Field(type => String)
  buttonFontColor!: string;

  // outlined buttons (border + text), e.g. "Zurück", "Ohne Passwort anmelden"
  @Field(type => String)
  outlineButtonColor!: string;
}
